// Add or edit ONE billing line, with a live server-computed preview.
//
// ⚠️ THE BROWSER NEVER DECIDES MONEY. This sends only {billing_type_id, price,
// N, detail, fixedDF} and renders whatever /api/billing-quote returns. On save,
// the same figures are recomputed server-side by the same billingSplit function
// the quote used — so what the user confirmed is exactly what is written, and a
// tampered payload changes nothing. `billing.priceDoctor` IS the doctor's
// payroll (df_doctor/gen3 sums it and nothing recomputes it), which is why it is
// out of reach here.
//
// The DF cascade lives in Xano (percentDoctorCal). Deliberately NOT mirrored in
// JS: three copies of those rules already exist and disagree, and a fourth in
// the bundle would drift silently — and would only take effect on redeploy,
// whereas the server version applies the moment a rate changes.
//
// Promotion lines are read-only here. promotion_patient/add picks the next
// installment from a live COUNT of billing rows, so a hand-edited or hand-added
// promo line re-charges the patient. The server refuses them too.

import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Gift, Trash2, Stethoscope, Building2, RotateCcw, Lock, Users } from 'lucide-react'
import { apiGet, cachedGet, postJson, apiDelete } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import { canEditPastBilling } from '../../lib/role.js'
import { useMe } from '../../lib/me.js'
import { apiErrorText } from '../../lib/apiError.js'
import PromotionRedeemPanel from '../promotion/PromotionRedeemPanel.jsx'
import {
  Drawer, DrawerFooter, ConfirmDialog, Button, Field, SearchSelect, Segmented, Loading, Banner,
  useToast, fmtDate, fmtMoney,
} from '../../kit'
import { BILLING_WATCH, conflictFromError, conflictingFields, describeChange, pickExpected } from '../../lib/conflict.js'
import { asPackages, packageN, promoName, stepPriceLabel } from '../../lib/patientPromotion.js'
import { buildSlotMap, selectionRows, remainingAfter, redeemPayload, rowsByPackage, slotPrice } from '../../lib/promotionSlots.js'
import { bHasPromo, bName, sortBillingTypes, dayLocked, bkkDay } from '../../lib/billing.js'
import { EXTRA_PAYERS, normPayer, payerPickerOptions } from '../../data/reimbursementPayers.js'

// A labelled box. `computed` = the server's answer, never typed — grey, so the
// difference between "what I set" and "what that produced" is visible at a
// glance rather than explained in a note.
function Box({ label, icon, children, computed, narrow, action }) {
  return (
    <div className={`rounded-xl border px-3 py-2 ${computed ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-white'} ${narrow ? 'w-[112px]' : ''}`}>
      <div className="flex items-center justify-between gap-1 mb-0.5">
        <span className="inline-flex items-center gap-1 text-[11.5px] text-slate-400">{icon}{label}</span>
        {action}
      </div>
      {typeof children === 'string' || typeof children === 'number'
        ? <div className={`text-[15px] tabular-nums ${computed ? 'font-semibold text-slate-700' : 'text-slate-800'}`}>{children}</div>
        : children}
    </div>
  )
}

const BOX_INPUT = 'w-full bg-transparent text-[15px] text-slate-800 tabular-nums outline-none placeholder:text-slate-300 disabled:text-slate-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
const STEP = 'px-1.5 text-slate-500 hover:text-brand-700 disabled:opacity-30 disabled:cursor-not-allowed select-none'
// NOT fmtBill: that rounds to whole baht, and a DF is very often a .5 value
// (427.5, 445.5). It showed a DF of 428 beside a Clinic of −427 — two halves
// rounded in opposite directions, so the boxes no longer reconciled, and the
// number on screen was one the user had never entered and would never be saved.
// Editors show the real figure; the ledger table can round.
// TWO LAYERS OF CALCULATION (owner's model, 2026-08-15):
//
//   LAYER 1 — the billing_type changed (from empty or from another type).
//   Every preset on the catalogue row REPLACES the corresponding input:
//   price → ค่ารักษา, percentDoctor → %DF, fixedDF → DF, advancePay → ผู้จ่าย,
//   priceAdvance → เบิกย้อนหลัง. Whether the field is editable is irrelevant;
//   the preset wins. Fields the catalogue says nothing about stay EMPTY.
//
//   LAYER 2 — an input changed, the type did not. ค่ารักษา / จำนวน and %DF-or-DF
//   are PRIMARY; everything else follows from them. An empty DF or เบิกย้อนหลัง
//   displays whatever billingSplit computed, so a no-preset field still shows a
//   number without pretending the user chose it.
//
// The distinction that matters: a value IN the state is sent to the server and
// is the user's; a value merely DISPLAYED came back from the server and moves
// when its inputs move.

// Raw for an <input value=>: no thousands separators, which a number input rejects.
const clampPct = (v) => {
  if (String(v).trim() === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  return String(Math.min(100, Math.max(0, n)))
}

const numStr = (v) => (v == null ? '' : String(v))

// Up to 2 decimals, none when whole: 427.5 stays 427.5 (never 428 / 427.50).
const fmtBox = (v) => fmtMoney(v, { maxDecimals: 2 })

// 100 - 47.5 lands on 52.49999999999999 in binary floating point.
const round2 = (v) => Math.round(Number(v) * 100) / 100

export default function BillingLineEditor({ patientId, treatmentId, txDate, line, freshLine, promoRows, promoComplete = true, onClose, onSaved, onDeleted, onTakeLatest }) {
  const me = useMe()
  const toast = useToast()
  const editing = !!line?.id
  const promo = editing && bHasPromo(line)

  /* HAS THIS LINE MOVED SINCE THE DRAWER OPENED?
   *
   * `line` is a snapshot: the page copies the row into `editor` on the click that
   * opens this drawer and never re-derives it, so a colleague's write refreshes
   * the ledger behind the drawer while every figure inside it stays as it was.
   * Saving then recomputed the whole line from those stale inputs and the other
   * write was gone — no merge, no warning, only a `billing.log[]` entry nobody
   * reads. `freshLine` is the same row as the page currently holds; the
   * difference between the two is the warning.
   *
   * `serverMoved` is the same question answered by the proxy at save time, which
   * is the authority: it sees writes this page never heard about (Bubble's above
   * all, still production and still writing billing). Preferred when present. */
  const [serverMoved, setServerMoved] = useState(null)
  const [goneOnSave, setGoneOnSave] = useState(false)
  const moved = serverMoved ?? (editing ? conflictingFields(line, freshLine, BILLING_WATCH) : [])

  /* `freshLine === null` is the page saying "this visit is loaded and this line
     is not in it" — voided at another station, since the feed filters
     `active == true`. `undefined` means it could not tell (still loading, or the
     visit is outside this page's set) and must NOT be read as deleted. */
  const gone = goneOnSave || (editing && freshLine === null)

  // THE DAY LOCK. A line is writable only on its treatment's own Bangkok day;
  // outside that it is manager · GM · admin. Enforced in
  // functions/api/billing/_guard.js — this copy only greys the controls and says
  // why, so nobody fills in the whole form and meets a 403 at บันทึก.
  //
  // `txDate` falls back to the line's own `date`, which Xano copies from the
  // treatment on create. Neither present → bkkDay('') is '' ≠ today, so the
  // form locks. That is the correct failure: refusing a write we cannot date is
  // recoverable, and silently allowing one is not.
  const lineDay = bkkDay(txDate ?? line?.date)
  const locked = dayLocked(txDate ?? line?.date, canEditPastBilling(me))

  const [types, setTypes] = useState([])
  const [typeId, setTypeId] = useState(line?.billing_type_id ? String(line.billing_type_id) : '')
  const [price, setPrice] = useState(line?.price != null ? String(line.price) : '')
  const [n, setN] = useState(String(line?.N ?? 1))
  const [detail, setDetail] = useState(String(line?.detail ?? ''))
  const nNum = Math.max(1, Math.floor(Number(n) || 1))
  // Claim fields. `adv` is the amount the payer actually allows for the WHOLE
  // line — blank means "use the catalogue rate x N", which is what -1 tells Xano.
  const [adv, setAdv] = useState(line?.priceadvance != null && line.priceadvance !== 0 ? String(line.priceadvance) : '')
  const [claimRef, setClaimRef] = useState(String(line?.claimRef ?? ''))
  // Blank = let the cascade decide. Any number = a fixedDF override in baht,
  // which billingSplit already accepted; the old UI could only ever send 0.
  const [df, setDf] = useState(line?.fixed_DF && line?.priceDoctor != null ? String(line.priceDoctor) : '')
  const [pct, setPct] = useState(line?.fixed_DF ? '' : (line?.percentDoctor != null ? String(line.percentDoctor) : ''))
  // Holds the payer ITSELF — blank means no claim, full stop. The three-way
  // '' / '__NONE__' / name contract still exists on the wire (fn 63 needs to
  // tell "unset" from "deliberately none"), but the user never sees it: the
  // field is seeded from the billing_type and edited like any other value.
  const linePayer = String(line?.advancePay || '').trim()
  const [payer, setPayer] = useState(linePayer)
  // The missing third state. Without it there is no way to tell "empty because
  // nobody set it" from "empty because I just cleared it", so the display
  // fallback re-fills the box the instant you finish deleting — which is why
  // backspacing to blank appeared to snap back to the old number.
  const [touched, setTouched] = useState({})
  const mark = (k) => setTouched((t) => (t[k] ? t : { ...t, [k]: true }))

  const [quote, setQuote] = useState(null)
  const [quoting, setQuoting] = useState(false)
  const [err, setErr] = useState('')
  const [saving, setSaving] = useState(false)

  /* ── the two modes ──────────────────────────────────────────────────────────
     ปกติ writes an ordinary line through POST /api/billing (api 563).
     โปรโมชั่น redeems one installment through POST /api/promotion-redeem (Xano
     api 189) — a DIFFERENT endpoint that owns the package's installment counter,
     stamps promotion_id / promotion_patient_id (api 563 never writes either) and
     mints the seller's commission row. They share this drawer and nothing else:
     no field on the ปกติ form is sent in โปรโมชั่น mode, and no figure typed here
     could reach that endpoint if it were.
     Offered only when CREATING. An existing promotion line is not editable at all
     (see `promo` above) because moving its price desynchronises the counter. */
  const [mode, setMode] = useState('normal')
  const [packages, setPackages] = useState(null)
  const [promoPick, setPromoPick] = useState(null)
  const [redeeming, setRedeeming] = useState(false)
  const [confirmRedeem, setConfirmRedeem] = useState(false)
  const redeemInflight = useRef(false)
  const [armDel, setArmDel] = useState(false)   // the delete ConfirmDialog is open
  const [deleting, setDeleting] = useState(false)

  /* Did the USER change anything? The fields as this drawer opened them, so the
     kit Drawer can ask "ทิ้งการแก้ไข?" before a stray backdrop click or Esc
     throws typed figures away (it used to close silently). Captured on the first
     render, i.e. after the useState seeds above and before any effect: opening
     an existing line never seeds (see `seededFor`), and ใช้ค่าล่าสุด remounts
     the whole drawer, so this baseline is always the line on screen. */
  const opened = useRef({ typeId, price, n, detail, adv, claimRef, df, pct, payer })
  const dirty = mode === 'promotion'
    ? !!promoPick?.slots?.length
    : Object.entries({ typeId, price, n, detail, adv, claimRef, df, pct, payer })
      .some(([k, v]) => String(v) !== String(opened.current[k]))

  useEffect(() => {
    cachedGet('/api/billing-types').then((r) => setTypes(asList(r))).catch(() => {})
  }, [])

  // Only when the tab is actually opened — most lines are ordinary, and this is a
  // per-patient call with a treatment[] payload behind it.
  useEffect(() => {
    if (mode !== 'promotion' || packages !== null || !patientId) return
    apiGet('/api/promotion-patient', { patient_id: patientId })
      .then((r) => setPackages(asPackages(r)))
      .catch((e) => { setPackages([]); setErr(readErr(e)) })
  }, [mode, packages, patientId])

  /* ── which installments are already spent ──────────────────────────────────
     No endpoint reads this. `promoRows` is the patient's own ACTIVE billing rows,
     which the ledger behind this drawer already loaded — the same row set api 189
     counts, so the two cannot disagree. `promoComplete` is false when that walk hit
     its 4000-row cap, and an incomplete list must never be read as "these slots are
     free". Both are just forwarded; promotionSlots.js owns every judgement. */
  const promoRowsByPkg = useMemo(() => (promoRows ? rowsByPackage(promoRows) : null), [promoRows])

  // The picked package's slot map, rebuilt from the same inputs the panel used —
  // the confirm dialog and the write must not read a second, staler copy.
  const promoMap = useMemo(() => {
    if (!promoPick?.pkg) return null
    const rows = promoRowsByPkg ? (promoRowsByPkg.get(Number(promoPick.id)) || []) : null
    return buildSlotMap(promoPick.pkg, rows, { complete: promoComplete })
  }, [promoPick, promoRowsByPkg, promoComplete])

  const options = useMemo(() => {
    // Retired types stay selectable when the line already points at one — a
    // legacy line must remain editable without silently switching its type.
    //
    // SORTED BY BUCKET: the picker (kit SearchSelect) opens a new header wherever `group`
    // changes down the list, so an unsorted catalogue split one type across
    // several headers scattered through the list.
    const list = sortBillingTypes(types.filter((t) => t.active !== false || String(t.id) === typeId))
    return list.map((t) => ({
      value: String(t.id),
      label: t.name || t.nameEng || `#${t.id}`,
      group: t.type || 'อื่นๆ',
      // The payer rides on the hint line: picking a claim type is a different
      // decision from picking a cash one, and it is invisible from the name.
      hint: [t.advancePay ? `เบิก ${t.advancePay}` : '', t.receipt_type].filter(Boolean).join(' · '),
      search: [t.nameEng, t.receipt_type, t.receipt_typeEN, t.StatType, t.advancePay].filter(Boolean).join(' '),
    }))
  }, [types, typeId])

  // Is the SELECTED type a claim type? Drives both the extra fields and whether
  // advance_override is sent at all — Xano ignores it on a cash line, but not
  // sending it keeps the quote honest about what it used.
  const selectedType = useMemo(() => types.find((t) => String(t.id) === String(typeId)) || null, [types, typeId])
  // Every payer the catalogue knows about, so the list stays in step with Xano
  // rather than being a hardcoded copy that silently rots — PLUS the payers no
  // catalogue row can name (EXTRA_PAYERS: a company subsidy is billed on an
  // ordinary treatment type, so only the payer differs), PLUS whatever this line
  // already claims to, so a legacy or retired payer stays editable instead of
  // reading blank and silently clearing the claim on the next save.
  //
  // Keyed by the tolerant payer key, so "Tooth Taxi" and a catalogue row spelling
  // it "tooth taxi" collapse to ONE option — the first spelling seen wins, and
  // that is the catalogue's, which is what Xano already stores.
  const payerOptions = useMemo(() => {
    const seen = new Map()
    const add = (v) => {
      const s = String(v || '').trim()
      if (s && !seen.has(normPayer(s))) seen.set(normPayer(s), s)
    }
    for (const t of types) add(t.advancePay)
    for (const p of EXTRA_PAYERS) add(p)
    add(linePayer)
    // The leading blank is how a claim gets cleared — "no payer" is a real
    // choice here, not the absence of one. It has no group, so it sits above the
    // first section header.
    return [{ value: '', label: '— ไม่เบิก —' }, ...payerPickerOptions(seen.values())]
  }, [types, linePayer])

  const btPayer = String(selectedType?.advancePay || '').trim()
  const effPayer = payer
  const isClaim = !!effPayer
  // null = not supplied (fn 63's own sentinel is -999999999). A claim can NOT be
  // negative (owner, 2026-10-26 evening): the "partner discount billed to the payer"
  // once cited for it was a sign error. A discount or refund goes on ค่ารักษา instead.
  // A negative here blocks บันทึก and is never sent — fn 63 / api 183 / api 638 refuse
  // it server-side too.
  const advNum = adv.trim() === '' || !Number.isFinite(Number(adv)) ? null : Number(adv)
  const advNegative = isClaim && advNum != null && advNum < 0
  const dfNum = df.trim() === '' || !Number.isFinite(Number(df)) ? null : Number(df)
  // No rate and no amount, and the user has cleared it: there is no basis for a
  // DF, so DF and Clinic read as UNKNOWN rather than showing the catalogue
  // fallback the server would still compute.
  //
  // ⚠️ TESTS THE SERVER'S DF, NOT ITS PERCENTAGE. This read
  // `quote?.percentDoctor == null` until 2026-08-29, and that is null on EVERY
  // fixed-DF type BY DESIGN — fn 63 sets `pct_out = null` in both of its fixed
  // branches, because a fixed DF has no percentage describing it. So on
  // ถอนฟัน - เบิกประกันสังคม (billing_type 305, fixedDF 427.5, price 0) the guard
  // fired the moment ค่ารักษา or จำนวน was touched — those handlers clear the
  // seeded DF and mark it — and blanked a DF the quote had already answered
  // (427.5, df_source "billing_type.fixedDF"), along with its Clinic.
  // It could never fire in the case it was written for, either: where the
  // catalogue fixes NEITHER, api 561 runs percentDoctorCal and the quote comes
  // back with a non-null percentDoctor. Nought right cases, every wrong one.
  const dfUnset = touched.df && df.trim() === '' && pct.trim() === '' && quote?.priceDoctor == null
  // A share of the fee cannot exceed the fee or go below nothing. Clamped where
  // it is typed and again here, so nothing outside 0–100 can reach the server
  // even if the value arrives from a saved row.
  const pctNum = pct.trim() === '' || !Number.isFinite(Number(pct))
    ? null : Math.min(100, Math.max(0, Number(pct)))

  // WHO DECIDES THE DF, in priority order. The catalogue wins where it has an
  // opinion; only where it is silent does the user get to type one, and then
  // the two fields are mutually exclusive — a DF in baht and a DF percentage
  // cannot both be the source of truth for the same line.
  const has = (v) => v != null && String(v).trim() !== ''
  // Both are always editable — the catalogue only SEEDS them. They lock each
  // other and nothing else: a rate and an amount cannot both be the source of
  // truth for one line. EVERY employee may change them (Ram, 2026-10-14) — they
  // were manager-only here while the proxy never checked, so the gate stopped the
  // counter without protecting anything. A trainee who is unsure asks a senior
  // or a manager first; that rule is taught, not enforced.
  const pctDisabled = promo || dfNum != null
  const dfDisabled = promo || pctNum != null

  // The claim amount is the catalogue's whenever the catalogue states one.
  // Always editable; the catalogue only seeds it.
  const advDisabled = promo

  // RESET means two different things and the difference matters:
  //   creating → re-run LAYER 1, pulling the selected billing_type's presets in
  //   editing  → restore the values as SAVED on the record
  // A saved line's numbers are not necessarily its type's presets — someone
  // chose them — so "reset" must not quietly re-seed an edit from the catalogue.
  const skipSeed = useRef(false)
  function resetFields() {
    setTouched({})
    if (editing) {
      // Restoring the type can re-fire the layer-1 effect, which would then
      // overwrite everything below it with catalogue presets.
      if (String(typeId) !== String(line?.billing_type_id ?? '')) {
        skipSeed.current = true
        setTypeId(line?.billing_type_id ? String(line.billing_type_id) : '')
      }
      setPrice(line?.price != null ? String(line.price) : '')
      setN(String(line?.N ?? 1))
      setDetail(String(line?.detail ?? ''))
      setDf(line?.fixed_DF && line?.priceDoctor != null ? String(line.priceDoctor) : '')
      setPct(line?.fixed_DF ? '' : (line?.percentDoctor != null ? String(line.percentDoctor) : ''))
      setPayer(String(line?.advancePay || '').trim())
      setAdv(line?.priceadvance != null && line.priceadvance !== 0 ? String(line.priceadvance) : '')
      setClaimRef(String(line?.claimRef ?? ''))
      return
    }
    const t = selectedType
    const val = (v) => (v == null || String(v).trim() === '' ? '' : String(v))
    const perLine = (v) => (v == null || String(v).trim() === '' ? '' : String(Number(v) * nNum))
    setPrice(val(t?.price))
    setPct(val(t?.percentDoctor))
    setDf(perLine(t?.fixedDF))
    setPayer(String(t?.advancePay || '').trim())
    setAdv(perLine(t?.priceAdvance))
    setDetail('')
    setClaimRef('')
  }

  // Every override the server needs, in ONE place. The quote and the save both
  // spread this: they had already drifted once — save() was not sending
  // percent_override, so a typed %DF previewed correctly and then wrote the
  // cascade's rate instead. A preview that disagrees with the write is the one
  // failure this whole design exists to prevent.
  function overrides() {
    const o = {}
    if (price !== '') o.price = Number(price)
    if (dfNum != null) o.fixedDF = dfNum
    if (pctNum != null) o.percent_override = pctNum
    // Only an actual departure from the catalogue travels. '' with a catalogue
    // payer is the deliberate "not a claim" case, which needs the sentinel.
    if (payer !== btPayer) o.advance_pay_override = payer === '' ? '__NONE__' : payer
    if (isClaim && advNum != null && !advNegative) o.advance_override = advNum
    return o
  }

  // Changing the item changes who decides the DF and the claim, so the previous
  // answers to those questions are discarded rather than silently re-applied to
  // a different catalogue row.
  //
  // GUARDED BY THE TYPE ITSELF, not by a "is this the first run?" flag. It used
  // to be `const firstType = useRef(true)` + skip-once, and under React
  // StrictMode (dev) that is a live bug: StrictMode mounts, unmounts and remounts
  // the effect while the ref PERSISTS, so the second run saw `false`, treated the
  // mount as a type change and seeded from a `types` list that had not loaded yet
  // — blanking ค่ารักษา / %DF / DF / ผู้จ่าย / เบิกย้อนหลัง on every line opened
  // for edit. Invisible whenever the stored price equals the catalogue's, and
  // wrong the moment it does not: a ฿100 ค่าบริการทางการแพทย์ opened as an empty
  // field quoting the catalogue's ฿20, and saving would have written the ฿20.
  //
  // Comparing the type this effect last seeded FOR is idempotent: re-running with
  // the same typeId is a no-op however many times React calls it, while a real
  // change still seeds. Initialised to the line's own type so opening an existing
  // line never seeds at all.
  const seededFor = useRef(line?.billing_type_id != null ? String(line.billing_type_id) : '')
  useEffect(() => {
    if (seededFor.current === String(typeId)) return
    seededFor.current = String(typeId)
    // A reset-to-record set the type back deliberately; its values follow.
    if (skipSeed.current) { skipSeed.current = false; return }
    // LAYER 1. Every preset the catalogue states REPLACES the input, editable or
    // not. A field the catalogue says nothing about is left empty and falls to
    // layer 2 (the server computes it). fixedDF and priceAdvance are per-unit in
    // the catalogue but these boxes are per-LINE, so they seed multiplied by จำนวน.
    const t = types.find((x) => String(x.id) === String(typeId))
    const val = (v) => (v == null || String(v).trim() === '' ? '' : String(v))
    const perLine = (v) => (v == null || String(v).trim() === '' ? '' : String(Number(v) * nNum))
    setPrice(val(t?.price))
    setPct(val(t?.percentDoctor))
    setDf(perLine(t?.fixedDF))
    setPayer(String(t?.advancePay || '').trim())
    setAdv(perLine(t?.priceAdvance))
    setTouched({})
  }, [typeId])  // eslint-disable-line react-hooks/exhaustive-deps

  // Live preview, debounced. The server is the only thing that computes money.
  const timer = useRef(null)
  useEffect(() => {
    if (!typeId || promo) { setQuote(null); return }
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      setQuoting(true); setErr('')
      const body = { treatment_id: treatmentId, billing_type_id: Number(typeId), N: Number(n) || 1, ...overrides() }
      postJson('/api/billing-quote', body)
        .then((q) => setQuote(q))
        .catch((e) => { setErr(readErr(e)); setQuote(null) })
        .finally(() => setQuoting(false))
    }, 500)
    return () => clearTimeout(timer.current)
    // `detail` is deliberately NOT a dependency: it never reaches the quote body,
    // so including it fired an identical request on every keystroke in the note.
  }, [treatmentId, typeId, price, n, dfNum, pctNum, promo, isClaim, advNum, payer, btPayer])

  /* `force` is the user answering the conflict banner with เขียนทับ: send no
     `expected`, so the proxy stops asking and this drawer's figures win. It is
     deliberately a SECOND action — the first save is refused and the overwrite
     is chosen, never assumed. */
  async function save(force = false) {
    setSaving(true); setErr('')
    try {
      const body = { treatment_id: treatmentId, billing_type_id: Number(typeId), N: Number(n) || 1, detail, ...overrides() }
      if (isClaim) body.claimRef = claimRef
      if (editing) {
        body.billing_id = line.id

        /* TWO LOCKS, AND THEY COVER DIFFERENT THINGS.
           `checker` is Xano's own (api 563: `precondition $input.checker == 0 ||
           $input.checker == $_old.modified_timestamp`). It is atomic and free —
           but it can only fire on a row whose `modified_timestamp` is set, and
           only api 563 has ever set one. Measured 2026-08-19: 0 of 1,364,749
           rows carry it, because production billing is still written by Bubble
           and Bubble does not stamp the column. So on today's data it protects
           nothing, and it will never see a Bubble edit at all.
           `expected` is the proxy's content check, which compares the row's own
           VALUES and therefore works from the first edit and against every
           writer. It runs first and is what the user actually sees.
           Both are sent: the fingerprint for coverage, the checker to close the
           read-then-write gap the proxy cannot. */
        if (line.modified_timestamp) body.checker = Number(line.modified_timestamp)
        if (!force) {
          const expected = pickExpected(line, BILLING_WATCH)
          if (expected) body.expected = expected
        }
      }
      await postJson('/api/billing', body)
      toast.success('บันทึกแล้ว')
      onSaved?.()
      // The success path used to end at onSaved(), on the assumption that the
      // parent always unmounts this drawer. When anything stops that unmount the
      // button spins forever with nothing to click and no error to read — the
      // failure Ram hit on 2026-08-19. Clearing it here costs nothing (a state
      // set after unmount is a no-op in React 18) and removes the whole class.
      setSaving(false)
    } catch (e) {
      // A conflict is not a failure to apologise for — it is this drawer's own
      // warning, arriving from the one place that can be certain.
      const c = conflictFromError(e)
      if (c?.gone) { setGoneOnSave(true); setSaving(false); return }
      // An empty field list would hide the banner and leave the button reading
      // บันทึก, so the next click would re-send `expected` and 409 forever.
      if (c?.stale && c.fields.length) { setServerMoved(c.fields); setSaving(false); return }
      setErr(readErr(e))
      toast.error('บันทึกไม่สำเร็จ', { detail: readErr(e) })
      setSaving(false)
    }
  }

  // `gone` — the line was voided at another station, so there is no row left to
  // edit and the only honest action is to close. Overwriting is NOT offered:
  // resurrecting a line somebody deliberately voided is not a conflict the user
  // is equipped to resolve from inside this drawer.
  const canSave = !!typeId && !saving && !quoting && !promo && !!quote && !locked && !gone && !advNegative

  // ใช้สิทธิ์ — forwards ONLY ids. Every figure on the resulting line is computed
  // inside Xano api 189 against the live catalogue; nothing typed in this drawer
  // participates. See functions/api/promotion-redeem.js for why it cannot go
  // through POST /api/billing.
  const canRedeemNow = !!promoPick?.slots?.length && !redeeming && !locked
  async function redeem() {
    if (!canRedeemNow || redeemInflight.current) return
    redeemInflight.current = true
    setRedeeming(true); setErr('')
    try {
      // The slots go in ONE request and are written server-side, one after the
      // other. Looping here would leave a half-billed package behind any dropped
      // connection with nothing to tell the user which installments landed —
      // and each write moves the counter the next one reads.
      // `steps[]` when the positions are real addresses, `{repeat:N}` when they are
      // only a count — redeemPayload reads that off the same map the picker used,
      // so the drawer can never claim an address it did not actually derive.
      const res = await postJson('/api/promotion-redeem', {
        promotion_patient_id: promoPick.id,
        treatment_id: Number(treatmentId),
        patient_id: Number(patientId),
        ...redeemPayload(promoMap, promoPick.slots),
      })
      // A partial success is still a success for what landed: the ledger must be
      // reloaded before the error is shown, or the staff member sees a refusal
      // over a screen that does not yet list the lines that were written.
      if (res?.failed?.length) {
        onSaved?.({ keepOpen: true })
        setErr(partialText(res))
        toast.error('ใช้สิทธิ์ได้บางส่วน', { detail: partialText(res) })
        setPromoPick(null)
        setPackages(null)
        return
      }
      toast.success('บันทึกแล้ว')
      onSaved?.()
    } catch (e) {
      setErr(readErr(e))
      toast.error('ใช้สิทธิ์ไม่สำเร็จ', { detail: readErr(e) })
      // Anything may have landed before the failure, so nothing on screen can be
      // trusted — drop the cached packages so the panel re-reads the truth.
      setPromoPick(null)
      setPackages(null)
    } finally {
      redeemInflight.current = false
      setRedeeming(false)
      setConfirmRedeem(false)
    }
  }

  // Moved here from a trash icon on the row (2026-08-14): the row now opens this
  // editor, and a destructive action belongs where the line's numbers are visible,
  // not behind a 12px icon in a table. Soft delete — Xano flips `active`.
  // The ConfirmDialog stays open (busy) while the request runs; it closes on
  // failure, or with this drawer when onDeleted unmounts it.
  async function doDelete() {
    setDeleting(true); setErr('')
    try {
      // `treatment_id` is what lets the proxy date the line for the day lock —
      // the route itself carries only the billing id. It is not trusted there:
      // the named treatment's own line list must actually contain this id.
      await apiDelete(`/api/billing/${line.id}?treatment_id=${encodeURIComponent(treatmentId)}`)
      toast.success('ลบรายการแล้ว')
      onDeleted?.()
    } catch (e) {
      // The server refuses promotion lines with a Thai explanation — surface it.
      const msg = apiErrorText(e, 'ลบไม่สำเร็จ')
      setErr(msg)
      toast.error('ลบไม่สำเร็จ', { detail: msg })
      setDeleting(false)
      setArmDel(false)
    }
  }

  // Why บันทึก / ใช้สิทธิ์ is dark, said in the footer (kit rule) instead of
  // leaving a disabled button to explain itself.
  const blockedWhy = mode === 'promotion'
    ? (redeeming ? null : locked ? 'รายการนี้ถูกล็อกแล้ว' : !promoPick?.slots?.length ? 'เลือกครั้งที่จะใช้สิทธิ์ก่อน' : null)
    : (canSave || saving || quoting ? null
      : gone ? 'รายการนี้ถูกลบไปแล้ว'
        : promo ? 'รายการโปรโมชั่นแก้ไขจากหน้านี้ไม่ได้'
          : locked ? 'รายการนี้ถูกล็อกแล้ว'
            : !typeId ? 'เลือกรายการก่อนบันทึก'
              : advNegative ? 'ยอดเบิกย้อนหลังติดลบไม่ได้'
                : !quote ? 'ยังคำนวณยอดไม่ได้' : null)

  return (
    <Drawer
      onClose={onClose}
      width="sm"
      title={editing ? 'แก้ไขรายการ Billing' : 'เพิ่มรายการ Billing'}
      busy={saving || redeeming || deleting}
      dirty={dirty}
      /* The row's own record (#id · สร้าง · แก้ไข), pinned bottom-left by the
         kit — on promotion and locked lines too: those are exactly the ones
         someone comes back to ask about. A new line passes nothing. */
      record={editing ? line : undefined}
      recordTable="billing"
      footer={(
        <DrawerFooter
          /* Voiding is a billing write like any other, so it obeys the same
             day lock — a locked line shows no ลบ button at all rather than a
             dead one, since the notice above already says why. */
          danger={editing && !locked && (
            <Button variant="danger-soft" size="sm" icon={Trash2} loading={deleting} disabled={saving}
              onClick={() => setArmDel(true)}>ลบรายการ</Button>
          )}
          secondary={mode === 'normal' && !!typeId && !promo ? (
            <Button variant="ghost" size="sm" icon={RotateCcw} onClick={resetFields} disabled={saving}
              title={editing ? 'กลับไปเป็นค่าที่บันทึกไว้' : 'ดึงค่าตั้งต้นของรายการนี้มาใหม่'}>
              {editing ? 'คืนค่าเดิม' : 'ค่าตั้งต้น'}
            </Button>
          ) : null}
          status={blockedWhy}
          onCancel={onClose}
          {...(mode === 'promotion' ? {
            onSave: () => setConfirmRedeem(true),
            saving: redeeming,
            canSave: canRedeemNow,
            saveLabel: promoPick?.slots?.length > 1 ? `ใช้สิทธิ์ ${promoPick.slots.length} ครั้ง` : 'ใช้สิทธิ์',
          } : {
            onSave: () => save(moved.length > 0),
            saving,
            canSave,
            saveLabel: moved.length > 0 ? 'บันทึกทับ' : 'บันทึก',
          })}
        />
      )}
    >
      <div className="space-y-4">
        {/* Two modes, create only. They are not two views of one form — each posts
            to a different endpoint (see the `mode` note above), so switching is a
            switch of what will be written, not of how it is displayed. Disabled
            mid-write so the target cannot change under a running request. */}
        {!editing && (
          <Segmented
            value={mode}
            onChange={(k) => { setMode(k); setErr('') }}
            disabled={saving || redeeming}
            options={[
              { value: 'normal', label: 'ปกติ' },
              { value: 'promotion', label: 'โปรโมชั่น', icon: Gift },
            ]}
          />
        )}

        {/* SOMEONE ELSE WROTE THIS LINE. At the TOP, unlike the day-lock notice
            at the bottom: the lock is a standing property of the record and the
            greyed controls already say so, while this is news that arrives
            mid-read and must not need scrolling to. Amber, not red — nothing
            has gone wrong yet, and the point is to hand the user the choice
            rather than make it. Auto-refreshing these boxes would be the other
            silent answer, and moving money under a hand that is mid-edit is the
            worse of the two. */}
        {gone ? (
          <Banner tone="error">
            รายการนี้ถูกลบไปแล้วจากอีกเครื่องหนึ่ง — ปิดหน้าต่างนี้แล้วตรวจสอบรายการล่าสุด
          </Banner>
        ) : moved.length > 0 && (
          <Banner tone="warn" icon={Users} title="มีคนแก้ไขรายการนี้ระหว่างที่หน้าต่างนี้เปิดอยู่"
            action={onTakeLatest ? <Button variant="secondary" size="sm" onClick={onTakeLatest} disabled={saving}>ใช้ค่าล่าสุด</Button> : null}>
            <ul className="list-disc ml-4 space-y-0.5 tabular-nums">
              {moved.map((m) => <li key={m.field}>{describeChange(m)}</li>)}
            </ul>
            <div className="mt-1 text-[12px]">หรือกด “บันทึกทับ” เพื่อใช้ค่าที่กรอกไว้</div>
          </Banner>
        )}

        <div className="rounded-2xl bg-white border border-slate-200 p-4 space-y-4">
          {mode === 'promotion' ? (
            <PromotionRedeemPanel
              packages={packages}
              types={types}
              rows={promoRowsByPkg}
              complete={promoComplete}
              value={promoPick}
              onChange={setPromoPick}
            />
          ) : (<>
          {promo && (
            <Banner tone="info" icon={Gift}>
              รายการนี้มาจากโปรโมชั่น <strong>{bName(line)}</strong> — แก้ไขราคาหรือ DF จากหน้านี้ไม่ได้
              เพราะจะทำให้จำนวนครั้งของแพ็กเกจคลาดเคลื่อน
              <div className="mt-1.5 text-[12.5px]">
                ถ้าใช้สิทธิ์ผิด ให้กด “ลบรายการ” ด้านล่าง — ระบบจะคืนสิทธิ์ครั้งนี้ให้คนไข้
                และถ้าเป็นครั้งสุดท้ายของแพ็กเกจ ค่าคอมมิชชั่นของผู้ขายจะถูกลบไปด้วย
              </div>
            </Banner>
          )}

          <Field label="รายการ" required>
            <SearchSelect
              value={typeId}
              onChange={setTypeId}
              options={options}
              disabled={promo}
              placeholder="ค้นหารายการ…"
            />
          </Field>

          <Box label="รายละเอียด (โน้ตของรายการนี้)">
            <input className={BOX_INPUT} value={detail} disabled={promo}
              onChange={(e) => setDetail(e.target.value)} placeholder="เช่น ซี่ 17" />
          </Box>

          {/* Labelled boxes, mirroring the form staff already know from Bubble:
              editable on white, SERVER-COMPUTED on grey. Nothing grey here is
              typed by anyone — it is what billingSplit answered for the values
              above it, refreshed on every keystroke. */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
            <Box label="ค่ารักษา">
              {/* LAYER 2: ค่ารักษา is primary. With a %DF the DF recomputes from
                  it; with no %DF there is NO rule that can, so a DF carried over
                  from the catalogue now describes a price that no longer exists.
                  Blank it rather than leave a stale number looking authoritative. */}
              <input className={BOX_INPUT} type="number" value={price} disabled={promo}
                onChange={(e) => { if (df.trim() !== '') { mark('df'); setDf('') } setPrice(e.target.value) }}
                placeholder={String(selectedType?.price ?? '0')} />
            </Box>
            <Box label="จำนวน" narrow>
              <div className="flex items-stretch">
                <button type="button" disabled={promo || nNum <= 1} onClick={() => { if (df.trim() !== '') { mark('df'); setDf('') } setN(String(Math.max(1, nNum - 1))) }}
                  className={STEP} aria-label="ลดจำนวน">−</button>
                <input className={`${BOX_INPUT} w-10 text-center`} type="number" min="1" value={n} disabled={promo}
                  onChange={(e) => { if (df.trim() !== '') { mark('df'); setDf('') } setN(e.target.value) }}
                  onBlur={() => setN(String(nNum))} />
                <button type="button" disabled={promo} onClick={() => { if (df.trim() !== '') { mark('df'); setDf('') } setN(String(nNum + 1)) }}
                  className={STEP} aria-label="เพิ่มจำนวน">+</button>
              </div>
            </Box>
            <Box label="Total" computed>{fmtBox(quote?.priceXunit)}</Box>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Box label="DF (%)" icon={<Stethoscope size={12} />} computed={pctDisabled}>
              {pctDisabled
                ? (quote?.percentDoctor != null ? `${quote.percentDoctor}%` : '—')
                : <input className={BOX_INPUT} type="number" min="0" max="100"
                    value={touched.pct ? pct : (pct !== '' ? pct : numStr(quote?.percentDoctor))}
                    onChange={(e) => { mark('pct'); setPct(clampPct(e.target.value)) }}
                    placeholder={quote?.percentDoctor != null ? `${quote.percentDoctor}` : '—'} />}
            </Box>
            <Box label="DF" icon={<Stethoscope size={12} />} computed={dfDisabled}>
              {dfDisabled
                ? fmtBox(quote?.priceDoctor)
                : <input className={BOX_INPUT} type="number"
                    value={touched.df ? df : (df !== '' ? df : numStr(quote?.priceDoctor))}
                    onChange={(e) => { mark('df'); setDf(e.target.value) }}
                    placeholder={dfUnset ? '' : fmtBox(quote?.priceDoctor)} />}
            </Box>
          </div>
          {pctNum != null && (
            <p className="text-[11.5px] text-slate-400 -mt-2">คิดจาก %DF ที่กรอก — ช่อง DF จึงแก้ไม่ได้</p>
          )}
          {dfNum != null && (
            <p className="text-[11.5px] text-slate-400 -mt-2">กำหนด DF เป็นจำนวนเงิน — %DF จึงว่างและแก้ไม่ได้</p>
          )}

          <div className="grid grid-cols-2 gap-2">
            {/* Never editable. %Clinic is blank whenever %DF is — a fixed DF has no
                percentage describing either side. Clinic is priceXunit − DF, so a
                DF larger than the patient price shows NEGATIVE, which is correct
                and is how a ปกส. line reads (patient pays 0, doctor still paid). */}
            <Box label="Clinic (%)" icon={<Building2 size={12} />} computed>
              {quote?.percentDoctor != null ? `${round2(100 - Number(quote.percentDoctor))}%` : '—'}
            </Box>
            <Box label="Clinic" icon={<Building2 size={12} />} computed>{dfUnset ? '—' : fmtBox(quote?.priceClinic)}</Box>
          </div>

          {/* The payer is a CHOICE, not a property of the catalogue row: a type that
              normally claims can be billed to the patient (__NONE__), and a cash
              type can be claimed. It decides whether priceActualTotal is the claim
              or the patient price — which is the DF base — so it sits above the
              amount, not beside it. */}
          {!promo && (
            <Field label="เบิกย้อนหลัง (ผู้จ่าย)">
              <SearchSelect
                value={payer}
                onChange={(v) => setPayer(v)}
                options={payerOptions}
                placeholder="— ไม่เบิก —"
                searchPlaceholder="พิมพ์ชื่อผู้จ่าย…"
              />
            </Field>
          )}

          {isClaim && !promo && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Box label="ผู้จ่าย" computed>{effPayer}</Box>
                <Box label="เบิกย้อนหลัง" computed={advDisabled}
                  action={!advDisabled && adv.trim() !== '' ? (
                    <button type="button" onClick={() => setAdv('')} title="กลับไปใช้อัตราตั้งต้น"
                      className="text-slate-400 hover:text-brand-600"><RotateCcw size={12} /></button>
                  ) : null}>
                  {advDisabled
                    ? fmtBox(quote?.priceadvance)
                    : <input className={BOX_INPUT} type="number" min="0"
                        value={touched.adv ? adv : (adv !== '' ? adv : numStr(quote?.priceadvance))}
                        onChange={(e) => { mark('adv'); setAdv(e.target.value) }}
                        placeholder={fmtBox(quote?.priceadvance)} />}
                </Box>
              </div>
              {advNegative && (
                <p className="text-[11.5px] text-rose-600 -mt-2">
                  ยอดเบิกย้อนหลังติดลบไม่ได้ — ถ้าเป็นส่วนลดหรือคืนเงิน ให้ใส่ค่ารักษาติดลบแทน
                </p>
              )}
              {advDisabled && (
                <p className="text-[11.5px] text-slate-400 -mt-2">
                  ประเภทนี้กำหนดอัตราเบิกไว้แล้ว ({selectedType.priceAdvance}/หน่วย) — เปลี่ยนผู้จ่ายถ้าต้องการกรอกเอง
                </p>
              )}
              <Box label="รหัสเคลมประกัน">
                <input className={BOX_INPUT} value={claimRef}
                  onChange={(e) => setClaimRef(e.target.value)} placeholder="เช่น EP2472258675" />
              </Box>
            </>
          )}

          {quoting && <Loading variant="inline" label="กำลังคำนวณ…" />}

          </>)}
        </div>

        {locked && (
          <Banner tone="info" icon={Lock} title="รายการนี้ถูกล็อกแล้ว">
            {lineDay
              ? `เป็นรายการของวันที่ ${fmtDate(lineDay)} ไม่ใช่วันนี้`
              : 'ไม่พบวันที่ของการรักษา'}
            <div className="text-[12px] mt-0.5">
              ยอดเงินของวันที่ปิดแล้วเข้าสู่รายงานรายได้และค่า DF ของหมอไปแล้ว —
              ถ้าต้องแก้จริง ให้ Admin เป็นผู้แก้
            </div>
          </Banner>
        )}

        {err && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-rose-700 text-[13px] flex items-start gap-2">
            <AlertTriangle size={14} className="flex-none mt-0.5" /><div>{err}</div>
          </div>
        )}
      </div>

      {confirmRedeem && promoPick && promoMap && (
        <ConfirmDialog
          title="ใช้สิทธิ์โปรโมชั่น"
          message={(
            <div className="space-y-1.5 text-[13.5px]">
              <CRow k="โปรโมชั่น" v={promoName(promoPick.pkg)} />
              {/* Every installment listed, one line each. A "3 ครั้ง" summary would
                  hide that they are not the same price — slot 1 of an ortho plan is
                  the 15,000 down payment and slot 2 is a 1,000 adjustment. */}
              {selectionRows(promoMap, promoPick.slots).map((s) => (
                <CRow key={s.ordinal}
                  k={`ครั้งที่ ${s.ordinal}${packageN(promoPick.pkg) ? ` / ${packageN(promoPick.pkg)}` : ''}`}
                  v={stepPriceLabel(slotPrice(s))} />
              ))}
              <p className="pt-1.5 text-[12.5px] text-slate-500">
                ระบบจะเพิ่มรายการ Billing {promoPick.slots.length} รายการเข้าการรักษานี้
                และตัดสิทธิ์ไป {promoPick.slots.length} ครั้ง — เหลืออีก {remainingAfter(promoMap, promoPick.slots)} ครั้ง
              </p>
            </div>
          )}
          confirmLabel="ใช้สิทธิ์"
          busy={redeeming}
          onConfirm={redeem}
          onCancel={() => setConfirmRedeem(false)}
        />
      )}
      {armDel && (
        <ConfirmDialog
          tone="danger"
          title="ลบรายการ Billing นี้?"
          message={(
            <>
              “{bName(line)}”{Number(line?.N) > 1 ? ` × ${line.N}` : ''} · {fmtMoney(line?.priceXunit)} บาท
              จะถูกลบออกจากการรักษานี้
              {promo && ' — ระบบจะคืนสิทธิ์ครั้งนี้ให้คนไข้ และถ้าเป็นครั้งสุดท้ายของแพ็กเกจ ค่าคอมมิชชั่นของผู้ขายจะถูกลบไปด้วย'}
            </>
          )}
          confirmLabel="ลบรายการ"
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setArmDel(false)}
        />
      )}
    </Drawer>
  )
}

function CRow({ k, v }) {
  return (
    <div className="flex gap-2">
      <span className="w-[72px] flex-none text-slate-400">{k}</span>
      <span className="text-slate-800">{v}</span>
    </div>
  )
}

// The proxies return a JSON body with a Thai `error`/`detail`; patchJson-style
// helpers stringify the whole thing into the message, so dig the readable part
// back out rather than showing raw JSON to staff.
// Xano answers {code, message, payload} and our proxies answer {error, detail} —
// apiErrorText reads all of them. The local copy this replaced checked only
// detail/error, so an ERROR_CODE_INPUT_ERROR (every precondition in the claude
// group) reached the front desk as raw JSON with the Thai sentence buried in it.
const readErr = (e) => apiErrorText(e, 'บันทึกไม่สำเร็จ')

// A batch of installments where some landed and some did not. It must name BOTH
// halves and it must name the slots: "เพิ่มไม่สำเร็จ" alone would send someone
// looking for lines that are already on the ledger, or leave them believing a
// refused installment was billed. The server's own Thai reason is kept verbatim —
// it is the only part that says WHY (a slot taken meanwhile, the day lock, a
// spent package).
function partialText(res) {
  const done = res?.added || []
  const bad = res?.failed || []
  // The installment numbers come back from Xano's own caption, and in forward mode
  // there is no number to quote — so the COUNT is always stated and the numbers
  // only when they are real. "ครั้งที่ null" is worse than no list at all.
  const nums = done.map((a) => a.slot).filter(Boolean)
  const okPart = `เก็บเงินสำเร็จ ${done.length} ครั้ง${nums.length === done.length ? ` (ครั้งที่ ${nums.join(', ')})` : ''}`
  const badPart = bad
    .map((f) => `${f.slot ? `ครั้งที่ ${f.slot}: ` : ''}${f.error || 'ไม่สำเร็จ'}`)
    .join(' · ')
  return `${okPart} — ไม่สำเร็จ ${bad.length} ครั้ง · ${badPart}`
}
